import { APP_CONFIG } from '@/constants/app';
import { tokenManager } from '@/utils/token-manager';
import axios from 'axios';
import type {
  Employee,
  EmployeeAddress,
  EmployeeBank,
  EmployeeContract,
  EmployeeDetails,
  EmploymentContractDetails,
  EmployeesListResponse,
  EmployeesQueryParams,
} from '../types';

const employeesApi = axios.create({
  baseURL: `${APP_CONFIG.api.baseUrl}/domains`,
  headers: {
    'Content-Type': 'application/json',
  },
});

tokenManager.setupInterceptors(employeesApi);

interface EmployeeMetadataDto {
  totalDocuments?: number | null;
  totalBulletins?: number | null;
}

interface EmployeeSummaryDto {
  uuid: string;
  remoteId?: number | null;
  folderUuid?: string | null;
  matriculeSalarie?: string | null;
  civilite?: number | null;
  nomUsage?: string | null;
  nomNaissance?: string | null;
  nom?: string | null;
  prenom?: string | null;
  email?: string | null;
  telephone?: string | null;
  dateNaissance?: string | null;
  isActive?: boolean | null;
  contratActif?: boolean | null;
  metadata?: EmployeeMetadataDto | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

interface EmployeeAddressDto {
  ligne1?: string | null;
  ligne2?: string | null;
  codePostal?: string | null;
  ville?: string | null;
  pays?: string | null;
  inseeCode?: string | null;
  codeDistributionEtranger?: string | null;
  fiscalResidentAbroad?: boolean | null;
  borderWorker?: boolean | null;
}

interface EmployeeBankDto {
  iban?: string | null;
  bic?: string | null;
  virement?: boolean | null;
}

interface EmployeeContractDto {
  uuid: string;
  remoteId: number;
  numeroContrat?: string | null;
  codeEtablissement?: string | null;
  establishmentUuid?: string | null;
  establishmentNom?: string | null;
  dateDebut?: string | null;
  dateFinPrevisionnelle?: string | null;
  dateFinEffective?: string | null;
  isActif: boolean;
  estCourant: boolean;
}

interface EmployeeDetailsDto extends EmployeeSummaryDto {
  folderUuid: string;
  nationalite?: string | null;
  communeNaissance?: string | null;
  departementNaissance?: string | null;
  paysNaissance?: string | null;
  numeroSecuriteSociale?: string | null;
  adresse?: EmployeeAddressDto | null;
  banque?: EmployeeBankDto | null;
  contrats: EmployeeContractDto[];
}

interface PaginatedEmployeesDto {
  data: EmployeeSummaryDto[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

interface EmployeeDetailApiResponse {
  data: EmployeeDetailsDto;
  meta?: {
    timestamp: string;
    path: string;
    statusCode: number;
  };
}

interface ContractScheduleDto {
  monthlyHours?: number | null;
  weeklyHours?: number | null;
  dailyHours?: {
    monday?: number | null;
    tuesday?: number | null;
    wednesday?: number | null;
    thursday?: number | null;
    friday?: number | null;
    saturday?: number | null;
    sunday?: number | null;
  } | null;
}

interface ContractTerminationDto {
  exitDate?: string | null;
  motive?: string | null;
  lastWorkedDay?: string | null;
  notificationDate?: string | null;
}

interface EmploymentContractDetailsDto extends EmployeeContractDto {
  employee?: EmployeeSummaryDto | null;
  employeeAdresse?: EmployeeAddressDto | null;
  employeeBanque?: EmployeeBankDto | null;
  employmentType?: number | null;
  partTimeType?: number | null;
  professionalStatus?: number | null;
  retirementScheme?: number | null;
  monthlySalary?: number | null;
  hourlySalary?: number | null;
  salarieTempsPartiel?: boolean | null;
  forfaitJour?: boolean | null;
  schedule?: ContractScheduleDto | null;
  termination?: ContractTerminationDto | null;
  metadata?: Record<string, unknown> | null;
}

interface EmploymentContractDetailsResponse {
  data: EmploymentContractDetailsDto;
  meta?: {
    timestamp: string;
    path: string;
    statusCode: number;
  };
}

const mapEmployeeSummaryDto = (dto: EmployeeSummaryDto): Employee => {
  return {
    uuid: dto.uuid,
    remoteId: dto.remoteId ?? null,
    folderUuid: dto.folderUuid ?? null,
    matriculeSalarie: dto.matriculeSalarie ?? null,
    civilite: dto.civilite ?? null,
    nomUsage: dto.nomUsage ?? null,
    nomNaissance: dto.nomNaissance ?? null,
    prenom: dto.prenom ?? null,
    nom: dto.nom ?? null,
    email: dto.email ?? null,
    telephone: dto.telephone ?? null,
    dateNaissance: dto.dateNaissance ?? null,
    isActive: dto.isActive ?? null,
    contratActif: dto.contratActif ?? null,
    metadata: dto.metadata
      ? {
          totalDocuments: dto.metadata.totalDocuments ?? 0,
          totalBulletins: dto.metadata.totalBulletins ?? 0,
        }
      : { totalDocuments: 0, totalBulletins: 0 },
    createdAt: dto.createdAt ?? null,
    updatedAt: dto.updatedAt ?? null,
  };
};

const mapEmployeeAddressDto = (
  dto: EmployeeAddressDto | null | undefined
): EmployeeAddress | null => {
  if (!dto) {
    return null;
  }

  return {
    ligne1: dto.ligne1 ?? null,
    ligne2: dto.ligne2 ?? null,
    codePostal: dto.codePostal ?? null,
    ville: dto.ville ?? null,
    pays: dto.pays ?? null,
    inseeCode: dto.inseeCode ?? null,
    codeDistributionEtranger: dto.codeDistributionEtranger ?? null,
    fiscalResidentAbroad: dto.fiscalResidentAbroad ?? null,
    borderWorker: dto.borderWorker ?? null,
  };
};

const mapEmployeeBankDto = (
  dto: EmployeeBankDto | null | undefined
): EmployeeBank | null => {
  if (!dto) {
    return null;
  }

  return {
    iban: dto.iban ?? null,
    bic: dto.bic ?? null,
    virement: dto.virement ?? null,
  };
};

const mapEmployeeContractDto = (
  dto: EmployeeContractDto
): EmployeeContract => ({
  uuid: dto.uuid,
  remoteId: dto.remoteId,
  numeroContrat: dto.numeroContrat ?? null,
  codeEtablissement: dto.codeEtablissement ?? null,
  establishmentUuid: dto.establishmentUuid ?? null,
  establishmentNom: dto.establishmentNom ?? null,
  dateDebut: dto.dateDebut ?? null,
  dateFinPrevisionnelle: dto.dateFinPrevisionnelle ?? null,
  dateFinEffective: dto.dateFinEffective ?? null,
  isActif: dto.isActif,
  estCourant: dto.estCourant,
});

const mapContractScheduleDto = (
  dto: ContractScheduleDto | null | undefined
) => {
  if (!dto) {
    return null;
  }

  return {
    monthlyHours: dto.monthlyHours ?? null,
    weeklyHours: dto.weeklyHours ?? null,
    dailyHours: dto.dailyHours
      ? {
          monday: dto.dailyHours.monday ?? null,
          tuesday: dto.dailyHours.tuesday ?? null,
          wednesday: dto.dailyHours.wednesday ?? null,
          thursday: dto.dailyHours.thursday ?? null,
          friday: dto.dailyHours.friday ?? null,
          saturday: dto.dailyHours.saturday ?? null,
          sunday: dto.dailyHours.sunday ?? null,
        }
      : null,
  };
};

const mapContractTerminationDto = (
  dto: ContractTerminationDto | null | undefined
) => {
  if (!dto) {
    return null;
  }

  return {
    exitDate: dto.exitDate ?? null,
    motive: dto.motive ?? null,
    lastWorkedDay: dto.lastWorkedDay ?? null,
    notificationDate: dto.notificationDate ?? null,
  };
};

const mapEmploymentContractDetailsDto = (
  dto: EmploymentContractDetailsDto
): EmploymentContractDetails => ({
  ...mapEmployeeContractDto(dto),
  employee: dto.employee ? mapEmployeeSummaryDto(dto.employee) : null,
  employeeAdresse: mapEmployeeAddressDto(dto.employeeAdresse),
  employeeBanque: mapEmployeeBankDto(dto.employeeBanque),
  employmentType: dto.employmentType ?? null,
  partTimeType: dto.partTimeType ?? null,
  professionalStatus: dto.professionalStatus ?? null,
  retirementScheme: dto.retirementScheme ?? null,
  monthlySalary: dto.monthlySalary ?? null,
  hourlySalary: dto.hourlySalary ?? null,
  salarieTempsPartiel: dto.salarieTempsPartiel ?? null,
  forfaitJour: dto.forfaitJour ?? null,
  schedule: mapContractScheduleDto(dto.schedule),
  termination: mapContractTerminationDto(dto.termination),
  metadata: dto.metadata ?? null,
});

const mapEmployeeDetailsDto = (dto: EmployeeDetailsDto): EmployeeDetails => ({
  ...mapEmployeeSummaryDto(dto),
  folderUuid: dto.folderUuid,
  nationalite: dto.nationalite ?? null,
  communeNaissance: dto.communeNaissance ?? null,
  departementNaissance: dto.departementNaissance ?? null,
  paysNaissance: dto.paysNaissance ?? null,
  numeroSecuriteSociale: dto.numeroSecuriteSociale ?? null,
  adresse: mapEmployeeAddressDto(dto.adresse),
  banque: mapEmployeeBankDto(dto.banque),
  contrats: dto.contrats?.map(mapEmployeeContractDto) ?? [],
  metadata: dto.metadata
    ? {
        totalDocuments: dto.metadata.totalDocuments ?? 0,
        totalBulletins: dto.metadata.totalBulletins ?? 0,
      }
    : { totalDocuments: 0, totalBulletins: 0 },
});

class EmployeesService {
  async getEmployees(
    folderUuid: string,
    params?: EmployeesQueryParams
  ): Promise<EmployeesListResponse> {
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.keyword || params?.search)
      queryParams.append(
        'keyword',
        (params.keyword ?? params.search) as string
      );
    if (params?.establishmentUuid)
      queryParams.append('establishmentUuid', params.establishmentUuid);

    const queryString = queryParams.toString();

    const response = await employeesApi.get<PaginatedEmployeesDto>(
      `/folders/${folderUuid}/employees${queryString ? `?${queryString}` : ''}`
    );

    const payload = response.data;

    return {
      data: payload.data.map(mapEmployeeSummaryDto),
      page: payload.page,
      limit: payload.limit,
      total: payload.total,
      totalPages: payload.totalPages,
      hasNextPage: payload.hasNextPage,
      hasPreviousPage: payload.hasPreviousPage,
    };
  }

  async getEmployeeDetail(
    folderUuid: string,
    employeeUuid: string
  ): Promise<EmployeeDetails> {
    const response = await employeesApi.get<EmployeeDetailApiResponse>(
      `/folders/${folderUuid}/employees/${employeeUuid}`
    );

    return mapEmployeeDetailsDto(response.data.data);
  }

  /**
   * Crée un nouvel employé
   */
  async createEmployee(
    folderUuid: string,
    employeeData: Partial<EmployeeDetails>
  ): Promise<EmployeeDetails> {
    const response = await employeesApi.post<EmployeeDetailApiResponse>(
      `/folders/${folderUuid}/employees`,
      employeeData
    );
    return mapEmployeeDetailsDto(response.data.data);
  }

  /**
   * Met à jour un employé
   */
  async updateEmployee(
    employeeUuid: string,
    employeeData: Partial<EmployeeDetails>
  ): Promise<EmployeeDetails> {
    const response = await employeesApi.put<EmployeeDetailApiResponse>(
      `/employees/${employeeUuid}`,
      employeeData
    );
    return mapEmployeeDetailsDto(response.data.data);
  }

  /**
   * Supprime un employé
   */
  async deleteEmployee(employeeUuid: string): Promise<void> {
    await employeesApi.delete(`/employees/${employeeUuid}`);
  }

  async getContractDetail(
    folderUuid: string,
    contractUuid: string
  ): Promise<EmploymentContractDetails> {
    const response = await employeesApi.get<EmploymentContractDetailsResponse>(
      `/folders/${folderUuid}/contracts/${contractUuid}`
    );

    return mapEmploymentContractDetailsDto(response.data.data);
  }
}

export const employeesService = new EmployeesService();
